import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  IconArrow,
  MenuItem,
  MenuRenderer,
  Strong,
  Text,
  TooltipRenderer,
  useResponsiveValue,
} from 'braid-design-system';
import type { FC } from 'react';

import { Display } from 'src/libs/Display';
import { BUCKETS } from 'src/stores/JobApplicationStore/types';
import mapBucketTypeToBuckets from 'src/stores/JobApplicationStore/utils/mapBucketToBuckets';
import useBucketLabel from 'src/utils/hooks/useBucketLabel';

import translations from './.vocab';

interface MoveStatusMenuProps {
  currentBucket: BUCKETS;
  fullName: string;
  changeStatus: (bucket: BUCKETS) => Promise<void>;
}

export const MoveStatusMenu: FC<MoveStatusMenuProps> = ({
  currentBucket,
  fullName,
  changeStatus,
}) => {
  const { t } = useTranslations(translations);
  const { getLabelFromBucket } = useBucketLabel();
  const responsiveValue = useResponsiveValue();

  const menuItems = Object.values(BUCKETS)
    .filter((bucket) => bucket !== BUCKETS.UNKNOWN)
    .map((bucket) => {
      const label = getLabelFromBucket(bucket);

      return (
        <MenuItem
          data={{ cy: `status-menu-item-${bucket}` }}
          key={bucket}
          onClick={() => {
            if (currentBucket !== bucket) {
              changeStatus(bucket);
            }
          }}
        >
          {currentBucket === bucket ? <Strong>{label}</Strong> : label}
        </MenuItem>
      );
    });

  if (currentBucket !== BUCKETS.INBOX) {
    return (
      <MenuRenderer
        data={{ cy: 'status-menu' }}
        offsetSpace="xxsmall"
        width="small"
        placement={
          responsiveValue({ mobile: 'top', tablet: 'bottom' }) ?? 'bottom'
        }
        align={responsiveValue({ mobile: 'left', tablet: 'right' }) ?? 'right'}
        trigger={(triggerProps) => (
          <Button
            {...triggerProps}
            size="small"
            tone="neutral"
            variant="solid"
            aria-label={t('Move fullName from bucket', {
              fullName,
              bucket: getLabelFromBucket(mapBucketTypeToBuckets(currentBucket)),
            })}
            icon={<IconArrow direction="right" />}
            iconPosition="trailing"
          >
            {t('Move to')}
          </Button>
        )}
      >
        {menuItems}
      </MenuRenderer>
    );
  }

  return (
    <MenuRenderer
      data={{ cy: 'status-menu' }}
      placement={
        responsiveValue({ mobile: 'top', tablet: 'bottom' }) ?? 'bottom'
      }
      width="small"
      offsetSpace="xxsmall"
      align={responsiveValue({ mobile: 'left', tablet: 'right' }) ?? 'right'}
      trigger={(triggerProps) => (
        <>
          <Display mobile>
            <Button
              {...triggerProps}
              size="small"
              tone="neutral"
              variant="soft"
              aria-label={t('Move fullName to another status', {
                fullName,
              })}
              icon={<IconArrow direction="right" />}
              iconPosition="trailing"
            >
              {t('Move to')}
            </Button>
          </Display>
          <Display tablet desktop>
            <TooltipRenderer
              id="move-status-menu"
              tooltip={<Text>{t('Move to')}</Text>}
            >
              {({ triggerProps: tooltipTriggerProps }) => (
                <Box {...tooltipTriggerProps}>
                  <Button
                    {...triggerProps}
                    size="small"
                    tone="neutral"
                    variant="soft"
                    aria-label={t('Move fullName to another status', {
                      fullName,
                    })}
                  >
                    <IconArrow direction="right" />
                  </Button>
                </Box>
              )}
            </TooltipRenderer>
          </Display>
        </>
      )}
    >
      {menuItems}
    </MenuRenderer>
  );
};
